// Admin preview dialog: pick a dealer + template, run a dry-run compile,
// show the single compiled brief + any warnings / blocked reasons.
//
// Preview endpoints are server-enforced to dry-run — even if a future
// bug set dry_run=false here, the backend flips it back.

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { agentCompilerApi, dealersApi } from '../../../api';

export default function CompilePreviewDialog({ open, onOpenChange, defaultTemplateId }) {
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState('');
  const [templateId, setTemplateId] = useState(defaultTemplateId || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTemplateId(defaultTemplateId || '');
    setResult(null);
    (async () => {
      try {
        setDealers(await dealersApi.list());
      } catch (err) {
        toast.error('Failed to load dealers');
      }
    })();
  }, [open, defaultTemplateId]);

  const run = async () => {
    if (!dealerId || !templateId) {
      toast.error('Select a dealer and template');
      return;
    }
    setLoading(true);
    try {
      const res = await agentCompilerApi.preview({
        dealer_id: dealerId,
        template_id: templateId,
        dry_run: true,
      });
      setResult(res);
    } catch (err) {
      toast.error(err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Compile preview (dry run)</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Dealer</Label>
            <Select value={dealerId} onValueChange={setDealerId}>
              <SelectTrigger><SelectValue placeholder="Select a dealer" /></SelectTrigger>
              <SelectContent>
                {dealers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name} ({d.onboarding_status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="text-xs text-muted-foreground">
            Template id: <code>{templateId || 'none selected'}</code>
          </div>

          <Button disabled={loading || !dealerId || !templateId} onClick={run}>
            {loading ? 'Compiling…' : 'Run preview'}
          </Button>

          {result && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="font-semibold">Status:</span>{' '}
                {result.ok ? 'compiled' : 'blocked'} — warnings:{' '}
                {result.warnings?.length || 0}
              </div>

              {result.blocked_reasons?.length > 0 && (
                <div className="bg-red-50 border border-red-200 p-3 rounded">
                  <div className="font-semibold text-red-800 mb-1">Blocked</div>
                  <ul className="list-disc pl-5 text-sm text-red-800">
                    {result.blocked_reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}

              {result.warnings?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                  <div className="font-semibold text-yellow-800 mb-1">Warnings</div>
                  <ul className="list-disc pl-5 text-sm text-yellow-800">
                    {result.warnings.map((w, i) => (
                      <li key={i}><code>{w.code}</code> — {w.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.brief && (
                <div>
                  <div className="font-semibold mb-1">Compiled brief (single)</div>
                  <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded overflow-x-auto whitespace-pre-wrap">
                    {result.brief.full_text}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
